import {
  Alert,
  Button,
  Container,
  Header,
  Input,
  Modal,
  Select,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  TextContent,
  TokenGroup,
} from '@amzn/awsui-components-react-v3';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { UPDATE_SEALS } from '../../../services/container-management/queries';
import './style.css';
import { CmsContainer, Seal } from '../../../services/container-management/types';
import { parseInt } from 'lodash';

export type ContainerSealsProps = {
  container: CmsContainer;
  containerFullName: string;
};

export default function ContainerSeals(props: ContainerSealsProps): ReactElement {
  const { t } = useTranslation();
  const { container } = props;
  const { version } = container;
  const { seals } = container;
  const [sealString, setSealString] = useState<string>('');

  const [initialSealIds] = useState<Seal[] | undefined>(seals);
  const [sealIds, setSealIds] = useState<{ label: string }[]>([]);

  const [isUpdateSealsSucceeded, setIsUpdateSealsSucceeded] = useState(false);
  const [updateSealsError, setUpdateSealsError] = useState<string | null>(null);
  const [containerVersion, setContainerVersion] = useState<number>(version);
  const [sealRemovalModal, setSealRemovalModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState();
  const [removedSeal, setRemovedSeal] = useState<Seal>();

  const [callUpdateSeals, { loading: updateSealsLoading }] = useMutation(UPDATE_SEALS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      const tokenSealIds = result.updateSeals.seals?.map((seal: { sealId: string }) => ({
        label: seal?.sealId,
      }));
      if (tokenSealIds) {
        setSealIds(tokenSealIds);
        setIsUpdateSealsSucceeded(true);
        setContainerVersion(containerVersion + 1);
      }
    },
    onError: (error) => {
      setUpdateSealsError(error.message);
    },
  });

  useEffect(() => {
    // Populate sealIds on page load
    if (props.container?.seals) {
      const tokenSealIds = initialSealIds?.map((seal) => ({
        label: seal?.sealId,
        disabled: !container.isActive,
      }));
      if (tokenSealIds) {
        setSealIds(tokenSealIds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddedSeal = (addSeal: Seal): void => {
    setIsUpdateSealsSucceeded(false);
    setUpdateSealsError(null);
    callUpdateSeals({
      variables: {
        addedSeals: [addSeal],
        containerId: container.containerId,
        version: containerVersion,
      },
    });
  };

  const handleSealRemovalToken = (seal: Seal): void => {
    setIsUpdateSealsSucceeded(false);
    setUpdateSealsError(null);
    setSealRemovalModal(true);
    setRemovedSeal(seal);
  };

  const handleRemovedSeals = (): void => {
    callUpdateSeals({
      variables: {
        removedSeals: [removedSeal],
        containerId: container.containerId,
        version: containerVersion,
        reasonCode: parseInt(selectedOption.value),
      },
    });
  };

  const sealIdStringIsValid = () => sealString.match('^[A-Za-z0-9-]+$');

  const getSealRemovalModal = () =>
    !isUpdateSealsSucceeded ? (
      <Modal
        onDismiss={() => setSealRemovalModal(false)}
        visible={sealRemovalModal}
        header="Remove Seal?">
        {updateSealsLoading ? (
          <Spinner size="big" />
        ) : (
          <SpaceBetween direction="vertical" size="m">
            <Alert type="warning">
              {t('You are deleting the seal associated with this container')}
            </Alert>
            <TextContent>
              <p>
                <small>{t('You must provide a reason to delete this seal')}</small>
              </p>
            </TextContent>
            <Select
              data-test-id="SelectRemovalReason"
              selectedOption={selectedOption}
              onChange={({ detail }) => setSelectedOption(detail.selectedOption)}
              options={[
                { label: 'Seal Break', value: '301' },
                { label: 'Error Correction', value: '302' },
              ]}
            />
            <Button
              data-test-id="UpdateRemovalReason"
              variant="primary"
              onClick={() => handleRemovedSeals()}>
              {t('Update')}
            </Button>
          </SpaceBetween>
        )}
      </Modal>
    ) : (
      <Modal visible header={t('Remove Seal?')} onDismiss={() => window.location.reload()}>
        <Alert data-test-id="successAlert" type="success">
          {t(`Seal ${removedSeal?.sealId} removed successfully`)}
        </Alert>
      </Modal>
    );

  return (
    <div>
      <Container
        header={
          <Header data-test-id="header" variant="h2" description="">
            Seals IDs
          </Header>
        }>
        <SpaceBetween direction="vertical" size="xs">
          <div data-test-id="sealId-input-div" className="column">
            <div className="flex-align-center">
              <Input
                className="medium_input"
                onChange={({ detail }) => setSealString(detail.value)}
                value={sealString}
                // eslint-disable-next-line react/destructuring-assignment
                placeholder={t(`Scan or enter Seal IDs for ${props.containerFullName}`)}
                type="text"
                disabled={updateSealsLoading || !container.isActive}
                onKeyDown={({ detail }) => {
                  if (detail.keyCode === 13 && sealIdStringIsValid() && sealString.length >= 3) {
                    // update sealId on enter key press
                    handleAddedSeal({ sealId: sealString });
                    setSealString('');
                  }
                }}
              />
              {isUpdateSealsSucceeded && (
                <StatusIndicator type="success">Seals Updated</StatusIndicator>
              )}
              {updateSealsLoading && (
                <div>
                  <Spinner size="normal" />
                  <p> Updating Seals </p>
                </div>
              )}
              {updateSealsError && (
                <StatusIndicator type="error">{updateSealsError}</StatusIndicator>
              )}
            </div>
            {sealString.length > 0 && !sealIdStringIsValid() && (
              <small
                data-test-id="sealStringError"
                style={{
                  color: 'red',
                }}>
                {t('SealId provided is invalid')}
              </small>
            )}
            {sealString.length > 0 && sealString.length < 3 && (
              <small
                data-test-id="sealStringError"
                style={{
                  color: 'red',
                }}>
                {t('Enter atleast three characters')}
              </small>
            )}
            <TextContent>
              <p>
                <small> Add up to 10 Seal IDs. Valid charaters: A-Z,a-z,0-9,'-'</small>
              </p>
            </TextContent>
          </div>
          <div>
            <TokenGroup
              onDismiss={({ detail: { itemIndex } }) => {
                handleSealRemovalToken({ sealId: sealIds[itemIndex].label });
              }}
              items={sealIds}
            />
          </div>
        </SpaceBetween>
        {sealRemovalModal && getSealRemovalModal()}
      </Container>
    </div>
  );
}
